import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  deleteField,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import ChatContainer from "../Components/Chat/ChatContainer";
import { systemMessageService } from "../Services/systemMessageService";
import DictionaryLookup from "../Components/DictionaryLookup";
// Economy controls are hidden on this screen for now.
// import CoinBalance from "../Components/CoinBalance";
import CoinStore from "../Components/CoinStore";
import useCoinStore from "../stores/coinStore";
import PowerUpShop from "../Components/PowerUpShop";
import BottomSheet from "../Components/BottomSheet";
import Kbd from "../Components/Kbd";
import { finePointer } from "../utils/pointer";
import { applyPendingEffects } from "../Services/powerUpService";
import { reviewMultiplayerAnswers } from "../Services/multiplayerAiReviewService";
import analytics from "../Services/analyticsService";
// import { Zap } from "lucide-react";
import { AnswerPeek, CAST, PeekingMascot, PencilMascot, useRoomMascots } from "../Components/Mascot";

const REVIEW_DURATION_SEC = 120;

const CATEGORY_RULES = {
  name: "a person's name",
  animal: "an animal",
  place: "a city, country or other place",
  thing: "an object or thing",
};

const ruleFor = (category, letter) =>
  CATEGORY_RULES[category]
    ? `Each answer should be ${CATEGORY_RULES[category]} starting with ${letter}.`
    : `Each answer should fit "${category}" and start with ${letter}.`;

const howReviewWorks = (letter) => [
  `Look at each answer in this category. Does it fit, and does it start with ${letter}?`,
  "Tap Valid if it does, Invalid if it doesn't. You don't vote on your own answers.",
  "Go through every category. Your votes send on their own once you've voted on everything, or tap Submit All Votes.",
  "The most votes decide. Valid answers score 10, or 5 if someone else had the same one.",
];

const ReviewPhase = ({ roomData, currentRound, onReviewComplete }) => {
  const [reviewData, setReviewData] = useState(null);
  const [roomDocId, setRoomDocId] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(0);
  const [userVotes, setUserVotes] = useState({});
  const [timeLeft, setTimeLeft] = useState(REVIEW_DURATION_SEC);
  const [hasSubmittedVotes, setHasSubmittedVotes] = useState(false);
  const [allPlayersDone, setAllPlayersDone] = useState(false);
  const [categoryInputs, setCategoryInputs] = useState({});
  const [hasAnnouncedReview, setHasAnnouncedReview] = useState(false);
  const [showCoinStore, setShowCoinStore] = useState(false);
  const [showPowerUpShop, setShowPowerUpShop] = useState(false);
  const [showVotingHelp, setShowVotingHelp] = useState(false);
  const [aiReviewedCategories, setAiReviewedCategories] = useState([]);
  const [hostControlsRevealed, setHostControlsRevealed] = useState(false);
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(null);
  const autoSubmitArmedRef = useRef(false);
  const categoryTopRef = useRef(null);
  const shownCategoryRef = useRef(0);
  const startedAtTopRef = useRef(false);

  // The scroll position carries over from the play screen, so review used to
  // open part-way down with the category row out of sight.
  useEffect(() => {
    if (!reviewData || startedAtTopRef.current) return;
    startedAtTopRef.current = true;
    window.scrollTo(0, 0);
  }, [reviewData]);

  // Next/Previous sit below the answers, so on a short screen the new category
  // would load above the fold and the click looked like it did nothing.
  useEffect(() => {
    if (shownCategoryRef.current === currentCategory) return;
    shownCategoryRef.current = currentCategory;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    categoryTopRef.current?.scrollIntoView({
      block: "start",
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, [currentCategory]);

  const reviewKeysRef = useRef(null);
  const [focusedAnswerId, setFocusedAnswerId] = useState(null);

  useEffect(() => {
    setFocusedAnswerId(null);
  }, [currentCategory]);

  useEffect(() => {
    if (!focusedAnswerId) return;
    document
      .querySelector(`[data-answer-id="${CSS.escape(focusedAnswerId)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [focusedAnswerId]);

  // Desktop shortcuts: arrows switch category or answer, V / X vote. Skipped
  // below lg, while typing, and while a sheet or modal is open.
  useEffect(() => {
    const onKeyDown = (e) => {
      const s = reviewKeysRef.current;
      if (!s || s.locked || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      if (!window.matchMedia("(min-width: 1024px)").matches) return;
      if (e.target.closest?.("input, textarea, select, [contenteditable='true'], [role='dialog']")) return;
      if (document.querySelector("[role='dialog'][aria-modal='true']")) return;

      const moveAnswer = (dir) => {
        if (!s.ids.length) return;
        const at = Math.max(0, s.ids.indexOf(s.activeId));
        setFocusedAnswerId(s.ids[Math.min(s.ids.length - 1, Math.max(0, at + dir))]);
      };
      const vote = (isValid) => {
        if (!s.activeId) return;
        s.castVote(s.activeId, isValid);
        const next = s.ids.find(
          (id) => id !== s.activeId && s.userVotes[`${s.category}.${id}.${s.userId}`] === undefined
        );
        setFocusedAnswerId(next ?? s.activeId);
      };

      switch (e.key) {
        case "ArrowLeft": s.goTo(s.current - 1); break;
        case "ArrowRight": s.goTo(s.current + 1); break;
        case "ArrowUp": moveAnswer(-1); break;
        case "ArrowDown": moveAnswer(1); break;
        case "v": case "V": vote(true); break;
        case "x": case "X": vote(false); break;
        default: return;
      }
      e.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const currentUserId = localStorage.getItem("currentUserId");
  const currentRoomId = localStorage.getItem("currentRoomId");
  const isHost = roomData?.hostId === currentUserId;
  const mascotFor = useRoomMascots(roomDocId, roomData, currentUserId);
  const coinStore = useCoinStore();
  const { isPro } = coinStore;
  const coinStoreRef = useRef(coinStore);
  useEffect(() => { coinStoreRef.current = coinStore; });
  const hasCalculatedScoresRef = useRef(false);
  const aiReviewTriggeredRef = useRef(false);

  const reviewMode = roomData?.reviewMode || "peer";
  // Mobile rooms mark AI review as aiReviewEnabled; web rooms as reviewMode: "ai".
  const isAiReview = reviewMode === "ai" || roomData?.aiReviewEnabled === true;

  const DICT_MAX_CHECKS = 7;
  // Caps stack: per-game resets with each room; per-day resets at the
  // player's local midnight across every game played that day, so someone
  // playing several games in one day doesn't get 7 free lookups *each* time.
  // Uses local date parts, not toISOString() (always UTC) — a UTC key would
  // reset hours before or after the player's actual midnight depending on
  // their timezone.
  const DICT_DAILY_LIMIT = 15;
  const dictStorageKey = `dict_checks_${currentRoomId}`;
  const getLocalDayKey = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  // Scoped to the signed-in player, not just the date — a bare date key
  // means every account sharing this browser (a shared/family device, or
  // simply signing out and back in as someone else) shares one daily
  // allowance, so one player exhausting it silently starves the next.
  const [dictDayKeySuffix, setDictDayKeySuffix] = useState(getLocalDayKey());
  const dictDayKey = `dict_daily_${currentUserId}_${dictDayKeySuffix}`;

  // Pro users get unlimited checks; free users get min(per-game, per-day) left
  const [dictChecksLeft, setDictChecksLeft] = useState(() => {
    if (isPro) return Infinity;
    const gameSaved = localStorage.getItem(dictStorageKey);
    const perGame = gameSaved !== null ? parseInt(gameSaved, 10) : DICT_MAX_CHECKS;
    const daySaved = localStorage.getItem(dictDayKey);
    const usedToday = daySaved !== null ? parseInt(daySaved, 10) : 0;
    const perDay = Math.max(0, DICT_DAILY_LIMIT - usedToday);
    return Math.min(perGame, perDay);
  });

  // dictChecksLeft above is only computed once, at mount. If this screen
  // stays mounted across the player's local midnight, dictDayKeySuffix
  // (and therefore the day-scoped portion of the allowance) would never
  // re-evaluate, leaving an exhausted allowance stuck at 0 forever even
  // though a new day's quota should now be available. Poll for the day
  // rolling over and recompute when it does.
  useEffect(() => {
    const id = setInterval(() => {
      const current = getLocalDayKey();
      setDictDayKeySuffix((prev) => (prev !== current ? current : prev));
    }, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // A user upgrading to Pro while this screen stays mounted must get their
    // unlimited lookups immediately — bailing out here without touching
    // dictChecksLeft would leave it stuck at whatever finite value it had
    // (possibly 0, permanently disabling the button) despite the new
    // entitlement.
    if (isPro) {
      setDictChecksLeft(Infinity);
      return;
    }
    const gameSaved = localStorage.getItem(dictStorageKey);
    const perGame = gameSaved !== null ? parseInt(gameSaved, 10) : DICT_MAX_CHECKS;
    const daySaved = localStorage.getItem(dictDayKey);
    const usedToday = daySaved !== null ? parseInt(daySaved, 10) : 0;
    const perDay = Math.max(0, DICT_DAILY_LIMIT - usedToday);
    setDictChecksLeft(Math.min(perGame, perDay));
    // Only re-run when the day actually rolls over (or user/pro status
    // changes) — not on every render, and not on dictStorageKey, whose own
    // changes are already applied directly by handleDictCheckUsed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictDayKeySuffix, currentUserId, isPro]);

  const handleDictCheckUsed = () => {
    if (isPro) return; // no limit for Pro

    const gameSaved = localStorage.getItem(dictStorageKey);
    const perGame = gameSaved !== null ? parseInt(gameSaved, 10) : DICT_MAX_CHECKS;
    const daySaved = localStorage.getItem(dictDayKey);
    const usedToday = daySaved !== null ? parseInt(daySaved, 10) : 0;

    const nextPerGame = Math.max(0, perGame - 1);
    const nextUsedToday = usedToday + 1;
    const nextPerDay = Math.max(0, DICT_DAILY_LIMIT - nextUsedToday);
    const next = Math.min(nextPerGame, nextPerDay);

    localStorage.setItem(dictStorageKey, String(nextPerGame));
    localStorage.setItem(dictDayKey, String(nextUsedToday));
    setDictChecksLeft(next);

    analytics.track("dictionary_lookup_used", { free_lookups_remaining: next });
    if (next === 0) {
      analytics.track("dictionary_limit_hit", { round_number: currentRound });
    }
  };

  // Get the categories from room data or use default
  const categories = roomData?.categories || [
    "name",
    "animal",
    "place",
    "thing",
  ];
  const roundData = roomData?.rounds?.[currentRound];
  const reviewMissingPlayers = (roomData?.players || []).filter(
    (player) =>
      player.id !== currentUserId && !(roundData?.playersSubmitted || []).includes(player.id)
  );

  // First, get document ID for direct access
  useEffect(() => {
    if (!currentRoomId) return;

    const getDocId = async () => {
      try {
        const q = query(
          collection(db, "rooms"),
          where("roomId", "==", currentRoomId),
          limit(1)
        );

        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          setRoomDocId(querySnapshot.docs[0].id);
        }
      } catch (error) {
        console.error("Error getting room document ID:", error);
      }
    };

    getDocId();
  }, [currentRoomId]);
  // useEffect(() => {
  //   // Send system message when review phase starts (only once)
  //   if (roomDocId && currentRound && !hasAnnouncedReview) {
  //     const announceReview = async () => {
  //       try {
  //         await systemMessageService.reviewPhaseStarted(
  //           roomDocId,
  //           currentRound
  //         );
  //         setHasAnnouncedReview(true); // Add this state variable
  //       } catch (error) {
  //         console.warn("Failed to send review start message:", error);
  //       }
  //     };

  //     announceReview();
  //   }
  // }, [roomDocId, currentRound]);
  // Calculate scores function - FIXED to handle custom categories
  const calculateScores = useCallback(async () => {
    if (!roomDocId || !reviewData || !roundData || !roundData.letter) return;
    if (hasCalculatedScoresRef.current || roundData.isComplete) return;
    hasCalculatedScoresRef.current = true;

    const scores = {};
    const roundLetter = roundData.letter.toLowerCase();

    // Check if this is a special round
    const currentRound = roomData?.currentRound || 1;
    const specialRounds = roomData?.specialRounds || [];
    const currentSpecialRound = specialRounds.find(
      (sr) => sr.round === currentRound
    );

    // Get submission order and timing for special round effects
    let submissionOrder = [];
    let submissionTiming = {};
    if (currentSpecialRound) {
      submissionOrder = [...roundData.submissions].sort(
        (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt)
      );

      // Calculate submission timing (relative to round start)
      const roundStartTime = roundData.startedAt?.toDate
        ? roundData.startedAt.toDate()
        : new Date(roundData.startedAt);

      roundData.submissions.forEach((submission) => {
        const submitTime = submission.submittedAt?.toDate
          ? submission.submittedAt.toDate()
          : new Date(submission.submittedAt);
        const timeTaken = Math.floor((submitTime - roundStartTime) / 1000);
        submissionTiming[submission.userId] = timeTaken;
      });
    }

    // Initialize scores with all categories
    roundData.submissions.forEach((submission) => {
      const scoreObject = { total: 0, specialBonus: 0, specialEffect: null };
      categories.forEach((category) => {
        scoreObject[category] = 0;
      });
      scores[submission.userId] = scoreObject;
    });

    // Answers whose vote was tied and settled by a coin flip, so results can
    // say so: { [userId]: [category, ...] }. Saved on the round, not inside
    // scores, so nothing that totals a player's scores ever sees it.
    const coinFlips = {};

    // Calculate BASE scores for each category (same as before)
    categories.forEach((category) => {
      const answersMap = {};

      roundData.submissions.forEach((submission) => {
        const answer = (submission.answers[category] || "")
          .toLowerCase()
          .trim();
        const answerId = submission.userId;
        const votes = reviewData[category]?.find((item) => item.id === answerId)
          ?.votes || { valid: 0, invalid: 0 };

        if (!answer || !answer.startsWith(roundLetter)) {
          scores[answerId][category] = 0;
          return;
        }

        const tied = votes.valid === votes.invalid;
        if (tied) (coinFlips[answerId] ||= []).push(category);
        const isValid = votes.valid > votes.invalid || (tied && Math.random() > 0.5);
        if (!isValid) {
          scores[answerId][category] = 0;
          return;
        }

        if (!answersMap[answer]) answersMap[answer] = [];
        answersMap[answer].push(answerId);
      });

      Object.values(answersMap).forEach((userIds) => {
        const points = userIds.length === 1 ? 10 : 5;
        userIds.forEach((userId) => {
          if (scores[userId]) {
            scores[userId][category] = points;
          }
        });
      });
    });

    // Calculate base totals BEFORE special effects
    Object.keys(scores).forEach((userId) => {
      let total = 0;
      categories.forEach((category) => {
        total += scores[userId][category] || 0;
      });
      scores[userId].total = total;
    });

    // APPLY SPECIAL ROUND EFFECTS
    if (currentSpecialRound && submissionOrder.length > 0) {
      const firstSubmitter = submissionOrder[0]?.userId;
      const lastSubmitter = submissionOrder[submissionOrder.length - 1]?.userId;

      switch (currentSpecialRound.type) {
        case "first_double":
          if (scores[firstSubmitter]) {
            scores[firstSubmitter].total *= 2;
            scores[firstSubmitter].specialEffect =
              "You submitted first! double points for speed!";
          }
          break;

        case "first_half":
          if (scores[firstSubmitter]) {
            scores[firstSubmitter].total = Math.floor(
              scores[firstSubmitter].total * 0.5
            );
            scores[firstSubmitter].specialEffect =
              "You submitted too soon! Your score got halved!";
          }
          break;

        case "last_double":
          if (scores[lastSubmitter]) {
            scores[lastSubmitter].total *= 2;
            scores[lastSubmitter].specialEffect =
              "Last to submit but your score got doubled!";
          }
          break;

        case "last_half":
          if (scores[lastSubmitter]) {
            scores[lastSubmitter].total = Math.floor(
              scores[lastSubmitter].total * 0.5
            );
            scores[lastSubmitter].specialEffect =
              "Too slow. Score halved by 2!";
          }
          break;

        case "time_zones":
          Object.keys(scores).forEach((userId) => {
            const timeTaken = submissionTiming[userId] || 45;
            let bonus = 0;
            let effect = "";

            if (timeTaken <= 10) {
              bonus = 10;
              effect = "Submitted in under 10 seconds: +10 bonus!";
            } else if (timeTaken <= 20) {
              bonus = 5;
              effect = "Submitted in under 20 seconds: +5 bonus";
            } else {
              bonus = -5;
              effect = "Took over 30 seconds: -5 penalty";
            }

            scores[userId].specialBonus = bonus;
            scores[userId].total += bonus;
            scores[userId].specialEffect = effect;
          });
          break;

        case "point_thief":
          // Randomly pick one player to be the thief and one to be the victim
          const totalPlayerIds = Object.keys(scores);
          if (totalPlayerIds.length >= 2) {
            // Pick random thief
            const thiefId =
              totalPlayerIds[Math.floor(Math.random() * totalPlayerIds.length)];

            // Pick random victim (different from thief)
            const possibleVictims = totalPlayerIds.filter(
              (id) => id !== thiefId
            );
            const victimId =
              possibleVictims[
              Math.floor(Math.random() * possibleVictims.length)
              ];

            // Random stolen points (5-15)
            const stolenPoints = Math.floor(Math.random() * 11) + 5; // 5 to 15

            // Get thief and victim names
            const thiefName =
              roomData.players.find((p) => p.id === thiefId)?.name || "Someone";
            const victimName =
              roomData.players.find((p) => p.id === victimId)?.name ||
              "Someone";

            // Steal points
            scores[thiefId].total += stolenPoints;
            scores[victimId].total = Math.max(
              0,
              scores[victimId].total - stolenPoints
            );

            // Set individual messages
            scores[
              thiefId
            ].specialEffect = `Stole ${stolenPoints} points from ${victimName}!`;
            scores[
              victimId
            ].specialEffect = `Lost ${stolenPoints} points to ${thiefName}!`;

            // Set message for everyone else
            totalPlayerIds.forEach((id) => {
              if (id !== thiefId && id !== victimId) {
                scores[
                  id
                ].specialEffect = `${thiefName} stole ${stolenPoints} points from ${victimName}!`;
              }
            });
          }
          break;

        case "equality":
          // Calculate average score
          const totalPoints = Object.values(scores).reduce(
            (sum, score) => sum + score.total,
            0
          );
          const averageScore = Math.floor(
            totalPoints / Object.keys(scores).length
          );

          Object.keys(scores).forEach((userId) => {
            scores[userId].total = averageScore;
            scores[userId].specialEffect = "Everyone gets same score!";
          });
          break;

        case "chosen_one":
          // Pick random player to get 3x points
          const playerIds = Object.keys(scores);
          const luckyPlayer =
            playerIds[Math.floor(Math.random() * playerIds.length)];

          scores[luckyPlayer].total *= 3;
          scores[luckyPlayer].specialEffect =
            "Lucky you! Your score gets tripled!";

          // Others get a message too
          Object.keys(scores).forEach((userId) => {
            if (userId !== luckyPlayer) {
              scores[userId].specialEffect = null;
            }
          });
          break;
        case "round_equality":
          // Calculate average score for this round only
          const roundTotalPoints = Object.values(scores).reduce(
            (sum, score) => sum + score.total,
            0
          );
          const roundAverageScore = Math.floor(
            roundTotalPoints / Object.keys(scores).length
          );

          Object.keys(scores).forEach((userId) => {
            scores[userId].total = roundAverageScore;
            scores[userId].specialEffect =
              "Equality: Everyone gets same score!";
          });
          break;

        case "total_equality_average":
          // Calculate TOTAL game average across all completed rounds
          const playerTotals = {};
          roomData.players.forEach((player) => {
            playerTotals[player.id] = 0;
          });

          // Sum up all previous rounds + current round
          Object.values(roomData.rounds).forEach((round) => {
            if (round.scores) {
              Object.entries(round.scores).forEach(
                ([userId, categoryScores]) => {
                  if (playerTotals[userId] !== undefined) {
                    playerTotals[userId] += categoryScores.total || 0;
                  }
                }
              );
            }
          });

          // Add current round scores
          Object.keys(scores).forEach((userId) => {
            if (playerTotals[userId] !== undefined) {
              playerTotals[userId] += scores[userId].total;
            }
          });

          // Calculate average of all totals
          const allTotals = Object.values(playerTotals);
          const globalAverage = Math.floor(
            allTotals.reduce((sum, total) => sum + total, 0) / allTotals.length
          );

          // Set everyone's TOTAL GAME SCORE to the average
          // We do this by adjusting their current round score
          Object.keys(scores).forEach((userId) => {
            const currentTotal = playerTotals[userId];
            const adjustment = globalAverage - currentTotal;
            scores[userId].total += adjustment;
            scores[userId].specialEffect =
              "TOTAL EQUALITY: All scores averaged!";
          });
          break;

        case "total_equality_random":
          // Pick a random player's TOTAL game score and apply to everyone
          const randomPlayerTotals = {};
          roomData.players.forEach((player) => {
            randomPlayerTotals[player.id] = { name: player.name, total: 0 };
          });

          // Calculate everyone's current total (including this round)
          Object.values(roomData.rounds).forEach((round) => {
            if (round.scores) {
              Object.entries(round.scores).forEach(
                ([userId, categoryScores]) => {
                  if (randomPlayerTotals[userId]) {
                    randomPlayerTotals[userId].total +=
                      categoryScores.total || 0;
                  }
                }
              );
            }
          });

          // Add current round scores
          Object.keys(scores).forEach((userId) => {
            if (randomPlayerTotals[userId]) {
              randomPlayerTotals[userId].total += scores[userId].total;
            }
          });

          // Pick random player's total
          const allPlayerIds = Object.keys(randomPlayerTotals);
          const luckyPlayerId =
            allPlayerIds[Math.floor(Math.random() * allPlayerIds.length)];
          const luckyPlayerTotal = randomPlayerTotals[luckyPlayerId].total;
          const luckyPlayerName = randomPlayerTotals[luckyPlayerId].name;

          // Set everyone's total to the lucky player's total
          Object.keys(scores).forEach((userId) => {
            const currentTotal = randomPlayerTotals[userId].total;
            const adjustment = luckyPlayerTotal - currentTotal;
            scores[userId].total += adjustment;
            scores[
              userId
            ].specialEffect = `LOTTERY EQUALITY: Everyone gets ${luckyPlayerName}'s score!`;
          });
          break;
      }
    }

    // Apply power-up effects (after special round effects)
    const {
      powerUpScores,
      effectsLog,
      resolvedIds,
      shieldUpdates,
      refunds,
    } = applyPendingEffects(scores, roomData, currentRound);

    // Handle refunds for effects blocked by special rounds
    if (refunds.length > 0) {
      const coinStore = coinStoreRef.current;
      for (const refund of refunds) {
        if (refund.sourceUserId === localStorage.getItem("currentUserId")) {
          await coinStore.addCoins(refund.cost, { purpose: "power_up_refund" });
        }
      }
    }

    // Save scores to Firebase
    try {
      const roomRef = doc(db, "rooms", roomDocId);

      // Build the updated pendingEffects array (remove resolved effects)
      const updatedPendingEffects = (roomData.pendingEffects || []).filter(
        (e) => !resolvedIds.includes(e.id)
      );

      // Build shield updates
      const shieldUpdateFields = {};
      Object.entries(shieldUpdates).forEach(([userId, val]) => {
        shieldUpdateFields[`shields.${userId}`] = val;
      });

      await updateDoc(roomRef, {
        [`rounds.${currentRound}.scores`]: scores,
        [`rounds.${currentRound}.isComplete`]: true,
        [`rounds.${currentRound}.reviewCompleted`]: true,
        ...(Object.keys(coinFlips).length > 0 && {
          [`rounds.${currentRound}.coinFlips`]: coinFlips,
        }),
        ...(Object.keys(powerUpScores).length > 0 && {
          [`rounds.${currentRound}.powerUpScores`]: powerUpScores,
        }),
        ...(effectsLog.length > 0 && {
          [`rounds.${currentRound}.powerUpEffectsLog`]: effectsLog,
        }),
        pendingEffects: updatedPendingEffects,
        ...shieldUpdateFields,
        // [`rounds.${currentRound}.submissions`]: deleteField(),
        // [`rounds.${currentRound}.reviewVotes`]: deleteField(),
      });

      analytics.track("round_reviewed", {
        room_id: roomDocId,
        round_number: currentRound,
        forced_by_host: !!roundData.forcedByHost,
      });

      try {
        // Send system messages for special round effects
        if (currentSpecialRound) {
          await systemMessageService.specialRoundRevealed(
            roomDocId,
            currentRound,
            currentSpecialRound.type
          );

          // Send individual effect messages for affected players
          Object.keys(scores).forEach(async (userId) => {
            const playerScore = scores[userId];
            if (playerScore.specialEffect) {
              const player = roomData.players.find((p) => p.id === userId);
              if (player) {
                try {
                  await systemMessageService.specialRoundEffect(
                    roomDocId,
                    player.name,
                    playerScore.specialEffect
                  );
                } catch (error) {
                  console.warn("Failed to send special effect message:", error);
                }
              }
            }
          });
        }

        // Send review completion message
        // await systemMessageService.reviewPhaseEnded(roomDocId, currentRound);
      } catch (error) {
        console.warn("Failed to send review messages:", error);
      }

      return true;
    } catch (error) {
      console.error("Error saving scores:", error);
      return false;
    }
  }, [
    roomDocId,
    reviewData,
    roundData,
    categories,
    currentRound,
    roomData?.specialRounds,
  ]);

  // Reset score calculation guard when round changes
  useEffect(() => {
    hasCalculatedScoresRef.current = false;
    aiReviewTriggeredRef.current = false;
  }, [currentRound]);

  // AI review mode: host auto-scores once ALL players have submitted answers
  // (mirrors the GameFlow condition: submissions.length === players.length OR timeExpired).
  // NOTE: playersSubmitted tracks vote submissions — in AI mode nobody votes,
  // so we check roundData.submissions and roundData.timeExpired instead.
  useEffect(() => {
    if (!isAiReview || !isHost || !roomDocId || !roundData || aiReviewTriggeredRef.current) return;
    const submissions = roundData.submissions || [];
    const totalPlayers = roomData?.players?.length || 0;
    const allSubmitted = submissions.length >= totalPlayers;
    const timeExpired = !!roundData.timeExpired;
    if (!allSubmitted && !timeExpired) return;
    if (submissions.length === 0) return;

    aiReviewTriggeredRef.current = true;
    setAiReviewedCategories([]);
    const runAiReview = async () => {
      try {
        const scores = await reviewMultiplayerAnswers(
          roundData.letter,
          categories,
          submissions.map((s) => ({ userId: s.userId, playerName: s.playerName, answers: s.answers || {} })),
          (doneCat) => setAiReviewedCategories((prev) => [...prev, doneCat])
        );
        const roomRef = doc(db, "rooms", roomDocId);
        await updateDoc(roomRef, {
          [`rounds.${currentRound}.scores`]: scores,
          [`rounds.${currentRound}.isComplete`]: true,
          [`rounds.${currentRound}.reviewCompleted`]: true,
          [`rounds.${currentRound}.aiReviewed`]: true,
        });
        if (onReviewComplete) onReviewComplete();
      } catch (err) {
        console.error("[AI Review] Failed:", err);
        // Do NOT reset the ref — prevents a retry loop on repeated snapshots.
        // The fallback scoring in reviewMultiplayerAnswers handles AI failures gracefully.
      }
    };
    runAiReview();
  }, [isAiReview, isHost, roomDocId, roundData, categories, currentRound, roomData?.players, onReviewComplete]);

  // Update category inputs when room data changes
  useEffect(() => {
    if (roomData && roomData.categories) {
      const categories = roomData.categories;

      // Create a new inputs object with all categories
      const newInputs = {};
      categories.forEach((category) => {
        // Preserve existing values if they exist
        newInputs[category] = categoryInputs[category] || "";
      });

      setCategoryInputs(newInputs);
    }
  }, [roomData?.categories]);

  // Timer effect
  useEffect(() => {
    if (!roundData) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!hasSubmittedVotes) {
            submitVotes();
          }
          // Previously only auto-submitted the current player's own vote — if
          // someone else's client wasn't around to do the same, nothing ever
          // moved the round forward and everyone sat on the review screen
          // indefinitely. The host's client force-ends the round for everyone
          // once time is actually up, so the countdown is a real deadline.
          if (isHost && !allPlayersDone) {
            forceEndReview();
          }
          return 0;
        }

        // NEW: Show warning when time is running low and players are missing
        if (prev === 60 && isHost) {
          const submittedPlayers = roundData.playersSubmitted || [];
          const missingPlayers =
            roomData?.players?.filter(
              (player) => !submittedPlayers.includes(player.id)
            ) || [];

          if (missingPlayers.length > 0) {
            console.log(
              "⚠️ 1 minute left - missing players:",
              missingPlayers.map((p) => p.name)
            );
          }
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [roundData, hasSubmittedVotes, isHost, roomData?.players, allPlayersDone]);

  // Let the host finish their own reviewing before surfacing who's holding things
  // up, same as the play screen's host controls.
  useEffect(() => {
    if (reviewMissingPlayers.length === 0 || allPlayersDone) {
      setHostControlsRevealed(false);
      return;
    }
    if (hasSubmittedVotes) {
      setHostControlsRevealed(true);
      return;
    }
    const timer = setTimeout(() => setHostControlsRevealed(true), 15000);
    return () => clearTimeout(timer);
  }, [reviewMissingPlayers.length, allPlayersDone, hasSubmittedVotes]);

  // Helper function to handle round data updates

  const handleRoundDataUpdate = useCallback(
    (round) => {
      if (!round || !reviewData || !categories || !roomData?.players) return;

      setReviewData((prev) => {
        if (!round.reviewVotes) return prev;

        const updatedReviewData = { ...prev };
        let changed = false;

        categories.forEach((category) => {
          if (round.reviewVotes[category]) {
            updatedReviewData[category] = updatedReviewData[category]?.map(
              (item) => {
                const votes = round.reviewVotes[category][item.id] || {
                  valid: 0,
                  invalid: 0,
                  voters: [],
                };

                if (
                  votes.valid !== item.votes.valid ||
                  votes.invalid !== item.votes.invalid
                ) {
                  changed = true;
                  return { ...item, votes };
                }
                return item;
              }
            );
          }
        });

        return changed ? updatedReviewData : prev;
      });

      if (round.playersSubmitted) {
        const totalPlayers = roomData.players.length;
        const submittedPlayers = round.playersSubmitted.length;
        const missingPlayers = roomData.players.filter(
          (player) => !round.playersSubmitted.includes(player.id)
        );

        // NEW: Check if we should auto-submit for missing players
        const shouldAutoSubmit = timeLeft <= 30 && missingPlayers.length > 0;

        if (shouldAutoSubmit && isHost && !round.autoSubmittedMissing) {
          console.log(
            "Auto-submitting for missing players:",
            missingPlayers.map((p) => p.name)
          );
          autoSubmitMissingPlayers(missingPlayers);
          return; // Don't process completion yet, let the auto-submit trigger it
        }

        if (submittedPlayers >= totalPlayers && !allPlayersDone) {
          setAllPlayersDone(true);
          calculateScores().then(() => {
            if (onReviewComplete) onReviewComplete();
          });
        }
      }
    },
    [
      categories,
      roomData?.players,
      allPlayersDone,
      onReviewComplete,
      calculateScores,
      timeLeft,
      isHost,
    ]
  );

  // Process submissions into reviewData
  useEffect(() => {
    if (!roundData?.submissions || !categories) return;

    // Create a map to track unique players and their latest submission
    const playerSubmissionMap = new Map();

    // Process submissions to keep only the latest one per player
    roundData.submissions.forEach((submission) => {
      const existingSubmission = playerSubmissionMap.get(submission.userId);

      if (
        !existingSubmission ||
        new Date(submission.submittedAt) >
        new Date(existingSubmission.submittedAt)
      ) {
        playerSubmissionMap.set(submission.userId, submission);
      }
    });

    // Convert back to array of unique submissions
    const uniqueSubmissions = Array.from(playerSubmissionMap.values());

    const newReviewData = {};

    categories.forEach((category) => {
      newReviewData[category] = uniqueSubmissions.map((submission) => {
        return {
          id: submission.userId,
          answer:
            submission.answers && submission.answers[category] !== undefined
              ? submission.answers[category]
              : "",
          playerName: submission.playerName,
          votes: { valid: 0, invalid: 0, voters: [] },
          // DELETE_FLAG: Add debug info to track submission source
          submissionType: submission.forcedSubmission ? "forced" : "normal",
          missedDeadline: submission.missedDeadline || false,
        };
      });
    });

    setReviewData((prev) => {
      // Only update if the data has actually changed
      const prevPlayerIds = prev
        ? Object.values(prev)[0]
          ?.map((i) => i.id)
          .join(",")
        : "";
      const newPlayerIds = uniqueSubmissions.map((s) => s.userId).join(",");

      if (prevPlayerIds === newPlayerIds) return prev;

      console.log(
        "Review data updated with unique submissions:",
        uniqueSubmissions.length
      );
      return newReviewData;
    });
  }, [roundData?.submissions, categories]);

  // Listen for real-time vote updates
  useEffect(() => {
    if (!roomData?.id || !reviewData || !currentRound) return;

    let unsubscribe;
    const setupListener = async () => {
      if (roomDocId) {
        unsubscribe = onSnapshot(doc(db, "rooms", roomDocId), (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            handleRoundDataUpdate(data.rounds?.[currentRound]);
          }
        });
      } else {
        unsubscribe = onSnapshot(
          query(
            collection(db, "rooms"),
            where("roomId", "==", currentRoomId),
            limit(1)
          ),
          (querySnapshot) => {
            if (!querySnapshot.empty) {
              const data = querySnapshot.docs[0].data();
              handleRoundDataUpdate(data.rounds?.[currentRound]);
            }
          }
        );
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [roomDocId, currentRound, currentRoomId, handleRoundDataUpdate]);

  const autoSubmitMissingPlayers = async (missingPlayers) => {
    if (!isHost || !roomDocId) return;

    try {
      console.log(
        "Auto-submitting missing players:",
        missingPlayers.map((p) => p.name)
      );

      const roomRef = doc(db, "rooms", roomDocId);
      const allPlayerIds = roomData.players.map((p) => p.id);

      await updateDoc(roomRef, {
        [`rounds.${currentRound}.playersSubmitted`]: allPlayerIds,
        [`rounds.${currentRound}.autoSubmittedMissing`]: true,
        [`rounds.${currentRound}.missingPlayers`]: missingPlayers.map((p) => ({
          id: p.id,
          name: p.name,
          autoSubmittedAt: new Date(),
        })),
      });

      // Send system message about missing players
      try {
        await systemMessageService.playersAutoSubmitted(
          roomDocId,
          missingPlayers.map((p) => p.name)
        );
      } catch (error) {
        console.warn("Failed to send auto-submit message:", error);
      }
    } catch (error) {
      console.error("Error auto-submitting missing players:", error);
    }
  };

  // Force end review function for host — always available once shown, same as
  // the play screen's host controls, rather than gated behind a time threshold.
  const forceEndReview = async () => {
    if (!isHost || !roomDocId) return;

    try {
      // Add all players to the submittedPlayers list
      const allPlayerIds = roomData.players.map((player) => player.id);

      // Update directly using document reference
      const roomRef = doc(db, "rooms", roomDocId);

      await updateDoc(roomRef, {
        [`rounds.${currentRound}.playersSubmitted`]: allPlayerIds,
        [`rounds.${currentRound}.forcedByHost`]: true,
        [`rounds.${currentRound}.forcedFromReview`]: true, // DELETE_FLAG: Mark this was forced from review phase
      });

      // Calculate scores and proceed
      await calculateScores();

      if (onReviewComplete) onReviewComplete();
    } catch (error) {
      console.error("Error forcing end of review:", error);
    }
  };

  // Cast vote function
  const castVote = async (answerId, isValid) => {
    // Prevent voting on own answer
    if (answerId === currentUserId) {
      console.warn("Cannot vote on your own answer");
      return;
    }

    // Safety check for roomDocId
    if (!roomDocId) {
      console.error("Room document ID not available");
      return;
    }

    const category = categories[currentCategory];
    const voteKey = `${category}.${answerId}.${currentUserId}`;

    // Track user vote locally
    setUserVotes((prev) => ({
      ...prev,
      [voteKey]: isValid,
    }));

    // Update vote in Firebase
    try {
      const roomRef = doc(db, "rooms", roomDocId);
      const votePath = `rounds.${currentRound}.reviewVotes.${category}.${answerId}`;

      // Remove previous vote if exists
      const currentVotes = reviewData[category]?.find(
        (item) => item.id === answerId
      )?.votes || { valid: 0, invalid: 0, voters: [] };

      let newValid = currentVotes.valid;
      let newInvalid = currentVotes.invalid;
      let newVoters = [...currentVotes.voters];

      // Remove previous vote from this user
      if (newVoters.includes(currentUserId)) {
        const previousVote = userVotes[voteKey];
        if (previousVote === true) newValid--;
        if (previousVote === false) newInvalid--;
      } else {
        newVoters.push(currentUserId);
      }

      // Add new vote
      if (isValid) newValid++;
      else newInvalid++;

      // Build per-vote voter lists for vote reveal feature
      const currentVoteData = reviewData[category]?.find((item) => item.id === answerId) || {};
      let validVoters = [...(currentVoteData.validVoters || [])];
      let invalidVoters = [...(currentVoteData.invalidVoters || [])];
      // Remove previous vote from voter lists
      validVoters = validVoters.filter((id) => id !== currentUserId);
      invalidVoters = invalidVoters.filter((id) => id !== currentUserId);
      // Add to correct list
      if (isValid) validVoters.push(currentUserId);
      else invalidVoters.push(currentUserId);

      await updateDoc(roomRef, {
        [`${votePath}.valid`]: newValid,
        [`${votePath}.invalid`]: newInvalid,
        [`${votePath}.voters`]: newVoters,
        [`${votePath}.validVoters`]: validVoters,
        [`${votePath}.invalidVoters`]: invalidVoters,
      });
    } catch (error) {
      console.error("Error casting vote:", error);
    }
  };

  // Submit votes function - memoized to prevent dependency issues
  const submitVotes = useCallback(async () => {
    if (hasSubmittedVotes || !roomDocId) return;

    setHasSubmittedVotes(true);

    try {
      const roomRef = doc(db, "rooms", roomDocId);

      // Add this player to the list of players who have submitted
      await updateDoc(roomRef, {
        [`rounds.${currentRound}.playersSubmitted`]: arrayUnion(currentUserId),
      });
    } catch (error) {
      console.error("Error marking player as submitted:", error);
      setHasSubmittedVotes(false);
    }
  }, [hasSubmittedVotes, roomDocId, currentRound, currentUserId]);

  // Auto-submit once this player has voted on every answer that needs their vote
  // (own answers are skipped) — previously required remembering to click "Submit
  // All Votes" as a separate step from casting the votes themselves. Short delay
  // gives a moment to change a vote before it locks in.
  useEffect(() => {
    if (hasSubmittedVotes || !reviewData || autoSubmitArmedRef.current) return;

    const totalNeedingVote = categories.reduce((sum, category) => {
      const items = reviewData[category] || [];
      return sum + items.filter((item) => item.id !== currentUserId).length;
    }, 0);

    if (totalNeedingVote === 0) return;

    const votesGiven = Object.keys(userVotes).length;
    if (votesGiven < totalNeedingVote) return;

    autoSubmitArmedRef.current = true;
    setAutoSubmitCountdown(5);

    const countdownInterval = setInterval(() => {
      setAutoSubmitCountdown((prev) => (prev && prev > 1 ? prev - 1 : 0));
    }, 1000);

    const submitTimeout = setTimeout(() => {
      clearInterval(countdownInterval);
      submitVotes();
    }, 5000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(submitTimeout);
    };
  }, [reviewData, userVotes, categories, currentUserId, hasSubmittedVotes, submitVotes]);

  if (!reviewData) {
    return (
      <div className="min-h-screen bg-orange-50 p-4 flex justify-center items-center">
        <div className="text-red-800 text-xl">Loading review...</div>
      </div>
    );
  }

  if (isAiReview) {
    const totalCats = categories.length;
    const doneCats = aiReviewedCategories.length;
    const progressPct = totalCats > 0 ? Math.round((doneCats / totalCats) * 100) : 0;
    const currentlyReviewing = doneCats < totalCats ? categories[doneCats] : null;

    return (
      <div className="min-h-screen bg-orange-50 px-4 pt-16 pb-24 flex justify-center">
        <div className="w-full max-w-sm">
          <PeekingMascot mascot={CAST.namey} mood={currentlyReviewing ? "thinking" : "happy"} width={92} right={28}>
            <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden">
              <div className="px-5 pt-5 pb-4">
                <p className="text-xs font-bold uppercase tracking-wide text-red-500">
                  Round {currentRound} · Letter {roundData?.letter}
                </p>
                <h2 className="mt-1 text-xl font-bold text-gray-900">
                  {currentlyReviewing ? "Namey is checking answers" : "All checked!"}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {currentlyReviewing
                    ? "One category at a time. Results show up on their own."
                    : "Adding up the scores…"}
                </p>
              </div>

              <ul className="border-t border-orange-100">
                {categories.map((cat) => {
                  const done = aiReviewedCategories.includes(cat);
                  const active = cat === currentlyReviewing;
                  return (
                    <li
                      key={cat}
                      className={`flex items-center justify-between px-5 py-3 border-b border-orange-50 last:border-b-0 ${active ? "bg-red-50" : ""}`}
                    >
                      <span className={`capitalize font-semibold ${done ? "text-gray-800" : active ? "text-red-700" : "text-gray-400"}`}>
                        {cat}
                      </span>
                      {done ? (
                        <span className="flex items-center gap-1 text-sm font-semibold text-green-600">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-xs">✓</span>
                          Checked
                        </span>
                      ) : active ? (
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-red-600">
                          Reading
                          <span className="flex gap-0.5" aria-hidden="true">
                            {[0, 1, 2].map((i) => (
                              <span key={i} className="h-1.5 w-1.5 rounded-full bg-red-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                            ))}
                          </span>
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">Up next</span>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className="px-5 py-4 bg-orange-50/60">
                <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1.5">
                  <span>{doneCats} of {totalCats} categories</span>
                  <span>{progressPct}%</span>
                </div>
                <div className="h-2 bg-orange-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            </div>
          </PeekingMascot>
        </div>
      </div>
    );
  }

  const currentCategoryName = categories[currentCategory];
  const currentAnswers = reviewData[currentCategoryName] || [];
  const voteKeyFor = (category, id) => `${category}.${id}.${currentUserId}`;
  const votableIds = currentAnswers.filter((item) => item.id !== currentUserId).map((item) => item.id);
  const firstOpenId =
    votableIds.find((id) => userVotes[voteKeyFor(currentCategoryName, id)] === undefined) ??
    votableIds[0] ??
    null;
  const activeAnswerId = votableIds.includes(focusedAnswerId) ? focusedAnswerId : firstOpenId;
  const votesLeftIn = (category) =>
    hasSubmittedVotes
      ? 0
      : (reviewData[category] || []).filter(
          (item) => item.id !== currentUserId && userVotes[voteKeyFor(category, item.id)] === undefined
        ).length;
  reviewKeysRef.current = {
    locked: hasSubmittedVotes || showVotingHelp || showCoinStore || showPowerUpShop,
    ids: votableIds,
    activeId: activeAnswerId,
    category: currentCategoryName,
    current: currentCategory,
    userId: currentUserId,
    userVotes,
    castVote,
    goTo: (index) => setCurrentCategory(Math.min(categories.length - 1, Math.max(0, index))),
  };
  return (
    <div className="min-h-screen bg-orange-50 p-3 sm:p-6 pb-24 lg:pb-32">
      <div className="max-w-4xl lg:max-w-6xl mx-auto">
        {/* Header - Compact on mobile. Title and the coin/power-up/buy
            actions used to fight for the same row and got cramped together
            on narrow screens — split onto two clear rows instead. Bottom
            margin trimmed down from the timer-bar era: that bar used to
            fill this space, so the same margin now reads as a dead gap
            before the category pills. */}
        <div className="mb-2">
          <div className="mb-3 flex items-center gap-3">
            <PencilMascot mood="judging" size={40} label="Namey" />
            <div>
            <h1 className="text-xl sm:text-2xl font-bold text-red-800">
              Review Phase
            </h1>
            <p className="text-sm sm:text-base text-gray-700">
              Round {currentRound} • Letter:{" "}
              <span className="font-bold text-red-600">
                {roundData?.letter}
              </span>
            </p>
            </div>
            <button
              type="button"
              onClick={() => setShowVotingHelp(true)}
              aria-haspopup="dialog"
              aria-label="How review works"
              className="ml-auto lg:hidden w-10 h-10 shrink-0 rounded-full border border-red-200 bg-red-50 text-red-700 text-lg font-bold flex items-center justify-center hover:bg-red-100 transition-colors"
            >
              ?
            </button>
          </div>

          {/* Coins, power-ups and buy-coins are kept out of review so it stays about voting.
          <div className="flex items-center justify-end gap-1.5 mb-2">
            <CoinBalance />
            <button
              onClick={() => setShowPowerUpShop(true)}
              className="w-8 h-8 flex items-center justify-center bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors shadow-sm"
              aria-label="Power-Up Shop"
              title="Power-Up Shop"
            >
              <span className="text-white text-base leading-none"><Zap className="w-4 h-4 fill-current" /></span>
            </button>
            <button
              onClick={() => setShowCoinStore(true)}
              className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
              aria-label="Buy Coins"
            >
              <span className="text-white text-xl font-bold leading-none">
                +
              </span>
            </button>
          </div>
          */}
        </div>

        {/* Host controls — a single compact row instead of a full timer bar plus a
            separate waiting-for-players box. No general countdown shown here: a
            visible clock implies it'll move things along on its own, but only the
            host's client can actually force that, so showing it to every viewer
            was misleading. Held back until the host finishes their own reviewing
            (or 15s) so it doesn't compete with their own voting. */}
        {isHost && hostControlsRevealed && !allPlayersDone && (
          <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            <p className="flex-1 text-xs font-semibold text-amber-800 truncate">
              {reviewMissingPlayers.length > 0
                ? `${reviewMissingPlayers.length === 1
                  ? "1 player hasn't"
                  : `${reviewMissingPlayers.length} players haven't`
                } voted: ${reviewMissingPlayers.map((p) => p.name).join(", ")}`
                : "Waiting on votes to finish"}
            </p>
            <button
              onClick={forceEndReview}
              className="flex-shrink-0 px-3 py-1.5 bg-orange-600 text-white text-xs font-semibold rounded-md hover:bg-orange-700 transition-colors"
            >
              Force End
            </button>
          </div>
        )}

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-6 lg:items-start">
        <div ref={categoryTopRef} className="min-w-0 scroll-mt-3">
        {/* Category Progress - Horizontal scroll on mobile */}
        <div className="mb-4 sm:mb-6 flex items-center justify-center lg:hidden">
          <div className="overflow-x-auto pb-2 -mx-3 px-3">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((category, index) => (
                <React.Fragment key={category}>
                  <button
                    type="button"
                    onClick={() => !hasSubmittedVotes && setCurrentCategory(index)}
                    disabled={hasSubmittedVotes}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${hasSubmittedVotes ? "cursor-not-allowed opacity-80" : "cursor-pointer"} ${index === currentCategory
                      ? "bg-red-600 text-white"
                      : index < currentCategory
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      }`}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                    {index < currentCategory && " ✓"}
                  </button>
                  {index < categories.length - 1 && (
                    <div
                      className={`w-1 h-0.5 ${index < currentCategory ? "bg-green-300" : "bg-gray-300"
                        }`}
                    ></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Status messages */}
        {hasSubmittedVotes && !allPlayersDone && (
          <div className="bg-blue-50 text-blue-700 p-3 rounded-lg mb-4 text-sm text-center border border-blue-200">
            Votes submitted. Waiting for others...
          </div>
        )}

        {roundData?.forcedByHost && !roundData?.forcedFromReview && (
          <div className="bg-orange-50 text-gray-700 p-3 rounded-lg mb-4 text-xs text-center border border-orange-200">
            Some players didn't submit in time, so the host moved to review
          </div>
        )}

        {roundData?.forcedByHost && roundData?.forcedFromReview && (
          <div className="bg-yellow-50 text-yellow-700 p-3 rounded-lg mb-4 text-sm text-center border border-yellow-200">
            Review ended early by host
          </div>
        )}

        {/* Main Review Area */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="hidden lg:block mb-1 text-xs font-bold uppercase tracking-wide text-gray-500">
                Category {currentCategory + 1} of {categories.length}
              </p>
              <h2 className="text-lg sm:text-xl lg:text-3xl lg:font-black font-bold text-red-800">
                Are these{" "}
                <span className="uppercase">{currentCategoryName}s</span> valid?
              </h2>
              <p className="hidden lg:block mt-1 text-sm text-gray-600">
                {ruleFor(currentCategoryName, roundData?.letter)}
              </p>
            </div>{/* end heading block */}

            {/* Dictionary checks counter — commented out until UI is reworked */}
            {/* {!isPro && (
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${dictChecksLeft === 0
                    ? "bg-gray-200 text-gray-500"
                    : dictChecksLeft <= 2
                      ? "bg-amber-100 text-amber-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
              >
                {dictChecksLeft}/{DICT_MAX_CHECKS} lookups left
              </span>
            )} */}
          </div>{/* end justify-between */}

          <div className="space-y-3">
            {!currentAnswers || currentAnswers.length === 0 ? (
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <p className="text-gray-500 text-sm">
                  No answers for this category
                </p>
              </div>
            ) : (
              currentAnswers.map((item) => {
                const userVoteKey = `${currentCategoryName}.${item.id}.${currentUserId}`;
                const userVote = userVotes[userVoteKey];
                const totalVotes = item.votes.valid + item.votes.invalid;
                const isOwnAnswer = item.id === currentUserId;
                // Resolved once every other player has voted on it (the author can't).
                const eligibleVoters = Math.max(1, (roomData?.players?.length || 1) - 1);
                const resolved = totalVotes > 0 && (allPlayersDone || totalVotes >= eligibleVoters);
                const result = !resolved
                  ? null
                  : item.votes.valid > item.votes.invalid
                    ? "valid"
                    : item.votes.invalid > item.votes.valid
                      ? "invalid"
                      : "tie";
                const winningVotes = result === "invalid" ? item.votes.invalid : item.votes.valid;

                return (
                  <AnswerPeek
                    key={item.id}
                    mascot={mascotFor(item.id)}
                    vote={userVote}
                    result={result}
                  >
                  <div
                    data-answer-id={item.id}
                    className={`relative rounded-lg p-3 border-2 lg:flex lg:items-center lg:gap-6 lg:p-4 lg:scroll-mb-28 ${isOwnAnswer
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 bg-white"
                      } ${finePointer && item.id === activeAnswerId ? "lg:ring-2 lg:ring-red-300 lg:ring-offset-1" : ""}`}
                  >
                    {/* Answer and player info */}
                    <div className="mb-3 lg:mb-0 lg:flex-1 lg:min-w-0 lg:grid lg:grid-cols-[minmax(0,max-content)_auto_1fr] lg:items-center lg:gap-x-3 lg:gap-y-1.5">
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 break-words mb-1.5 lg:mb-0 lg:col-start-1 lg:row-start-1">
                        {item.answer || (
                          <span className="text-red-500 italic font-normal">
                            No answer
                          </span>
                        )}
                      </p>

                      <div className="flex items-center justify-between gap-2 lg:contents">
                        <span className="text-sm text-gray-600 truncate lg:col-span-3 lg:col-start-1 lg:row-start-2">
                          {item.playerName}
                          {isOwnAnswer && (
                            <span className="ml-1 text-red-600 font-semibold">
                              (You)
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2 shrink-0 lg:col-start-2 lg:row-start-1">
                          {item.answer && (
                            <DictionaryLookup
                              word={item.answer}
                              category={currentCategoryName}
                              checksLeft={dictChecksLeft}
                              onCheckUsed={handleDictCheckUsed}
                            />
                          )}
                        </div>
                      </div>
                      {result && (
                        <p
                          className={`mt-1 text-xs font-semibold lg:col-span-3 lg:row-start-3 ${result === "valid"
                            ? "text-green-700"
                            : result === "invalid"
                              ? "text-red-700"
                              : "text-gray-500"
                            }`}
                        >
                          {result === "valid" ? "✓ Valid" : result === "invalid" ? "✗ Invalid" : "Split vote"}
                          {" · "}
                          {result === "tie"
                            ? `${item.votes.valid}–${item.votes.invalid}`
                            : totalVotes === 1
                              ? "1 vote"
                              : `${winningVotes}/${totalVotes} votes`}
                        </p>
                      )}
                    </div>

                    {/* Vote buttons - Full width on mobile */}
                    <div className="flex gap-3 lg:w-80 lg:shrink-0">
                      {isOwnAnswer ? (
                        <div className="w-full text-xs sm:text-sm text-red-600 italic px-3 py-3 bg-red-50 rounded-lg text-center border border-red-200">
                          Can't vote on your own answer
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => { castVote(item.id, true); setFocusedAnswerId(null); }}
                            disabled={hasSubmittedVotes || !roomDocId}
                            className={`flex-1 px-4 py-3 rounded-lg font-bold transition-all text-sm sm:text-base ${userVote === true
                              ? "bg-green-600 text-white shadow-md scale-105"
                              : "bg-green-100 text-green-700 hover:bg-green-200 active:scale-95"
                              } ${hasSubmittedVotes || !roomDocId
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                              }`}
                          >
                            ✓ Valid
                          </button>
                          <button
                            onClick={() => { castVote(item.id, false); setFocusedAnswerId(null); }}
                            disabled={hasSubmittedVotes || !roomDocId}
                            className={`flex-1 px-4 py-3 rounded-lg font-bold transition-all text-sm sm:text-base ${userVote === false
                              ? "bg-red-600 text-white shadow-md scale-105"
                              : "bg-red-100 text-red-700 hover:bg-red-200 active:scale-95"
                              } ${hasSubmittedVotes || !roomDocId
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                              }`}
                          >
                            ✗ Invalid
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  </AnswerPeek>
                );
              })
            )}
            <div className="w-full mx-auto mt-6 lg:fixed lg:inset-x-0 lg:bottom-0 lg:z-30 lg:bg-white lg:border-t lg:border-orange-200 lg:pl-8 lg:pr-28 min-[1400px]:pr-8 lg:py-4">
              {autoSubmitCountdown !== null && !hasSubmittedVotes && (
                <p className="text-center text-sm font-semibold text-green-600 mb-3 lg:mb-2">
                  All votes cast — submitting in {autoSubmitCountdown}s…
                </p>
              )}
              <div className="flex w-full gap-4 flex-col justify-center items-center lg:flex-row lg:justify-between lg:max-w-6xl lg:mx-auto">
                <button
                  onClick={() =>
                    setCurrentCategory(Math.max(0, currentCategory - 1))
                  }
                  disabled={currentCategory === 0 || hasSubmittedVotes}
                  className="px-4 sm:px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors w-full lg:w-auto lg:min-w-[200px]"
                >
                  ← Previous
                </button>

                <p className="hidden lg:flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  <span>
                    <span className="font-semibold text-gray-700 capitalize">{currentCategoryName}</span>
                    {" "}· {currentCategory + 1} of {categories.length}
                  </span>
                  {finePointer && !hasSubmittedVotes && (
                    <span className="flex items-center gap-1.5 text-xs">
                      <Kbd>←</Kbd><Kbd>→</Kbd> category <Kbd>↑</Kbd><Kbd>↓</Kbd> answer <Kbd>V</Kbd> valid <Kbd>X</Kbd> invalid
                    </span>
                  )}
                </p>

                {currentCategory === categories.length - 1 ? (
                  <button
                    onClick={submitVotes}
                    disabled={hasSubmittedVotes || !roomDocId}
                    className={`px-6 py-3 w-full lg:w-auto lg:min-w-[240px] rounded-lg font-bold text-white transition-all ${hasSubmittedVotes || !roomDocId
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700 active:scale-95 shadow-md"
                      }`}
                  >
                    {hasSubmittedVotes ? "✓ Votes Submitted" : "Submit All Votes"}
                  </button>
                ) : (
                  <button
                    onClick={() =>
                      setCurrentCategory(
                        Math.min(categories.length - 1, currentCategory + 1)
                      )
                    }
                    disabled={hasSubmittedVotes}
                    className="px-4 sm:px-6 py-3 w-full lg:w-auto lg:min-w-[240px] bg-red-600 text-white rounded-lg font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700 transition-colors active:scale-95"
                  >
                    Next →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>{/* end main column */}
        <aside className="hidden lg:block lg:sticky lg:top-4 space-y-4">
          <div className="bg-white rounded-lg border-2 border-red-200 p-4">
            <p className="font-bold text-gray-900">Check everyone's answers</p>
            <p className="mt-1.5 text-sm text-gray-700 leading-relaxed">
              Go through each category. For every answer, tap{" "}
              <strong className="text-green-700">Valid</strong> if it fits and starts with{" "}
              <strong className="text-red-600">{roundData?.letter}</strong>, or{" "}
              <strong className="text-red-700">Invalid</strong> if it doesn't. The most votes
              decide who scores.
            </p>
          </div>
          <nav aria-label="Categories" className="bg-white rounded-lg shadow-sm p-2">
            {categories.map((category, index) => {
              const left = votesLeftIn(category);
              const done = left === 0;
              const isCurrent = index === currentCategory;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => !hasSubmittedVotes && setCurrentCategory(index)}
                  disabled={hasSubmittedVotes}
                  aria-current={isCurrent ? "step" : undefined}
                  className={`w-full h-12 px-3 rounded-lg flex items-center gap-3 text-left text-[15px] font-semibold transition-colors ${hasSubmittedVotes ? "cursor-not-allowed opacity-80" : "cursor-pointer"} ${isCurrent ? "bg-red-50 text-red-800" : "text-gray-800 hover:bg-gray-50"}`}
                >
                  <span
                    className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-extrabold ${done ? "bg-green-600 text-white" : isCurrent ? "bg-red-600 text-white" : "bg-gray-200 text-gray-600"}`}
                  >
                    {done ? "✓" : index + 1}
                  </span>
                  <span className="flex-1 capitalize">{category}</span>
                  <span className={`text-xs font-semibold ${done ? "text-green-700" : "text-amber-700"}`}>
                    {done ? "Done" : `${left} to vote`}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>
        </div>{/* end review grid */}
        {/* Navigation - Back to inline */}
      </div>

      <BottomSheet
        isOpen={showVotingHelp}
        onClose={() => setShowVotingHelp(false)}
        title="Check everyone's answers"
        zClass="z-[100]"
      >
        <ol className="space-y-4">
          {howReviewWorks(roundData?.letter).map((text, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 shrink-0 rounded-full bg-red-50 text-red-700 text-xs font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <p className="text-[15px] leading-snug text-gray-700">{text}</p>
            </li>
          ))}
        </ol>
        <button
          type="button"
          onClick={() => setShowVotingHelp(false)}
          className="mt-6 w-full h-12 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
        >
          Got it
        </button>
      </BottomSheet>

      {/* CoinStore Modal */}
      {showCoinStore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <CoinStore isOpen={true} onClose={() => setShowCoinStore(false)} />
          </div>
        </div>
      )}

      {/* PowerUpShop Modal */}
      {showPowerUpShop && (
        <PowerUpShop
          roomData={roomData}
          roomDocId={roomDocId}
          currentRound={currentRound}
          onClose={() => setShowPowerUpShop(false)}
        />
      )}
    </div>
  );
};

export default ReviewPhase;
